import {
    ClassroomAnalytics,
    ClassroomsClustering,
    ClusterInfosClustering,
    InfosEleves,
    ModuleCluster,
} from "../../interfaces/Dashboard";
/* import { ClusterItem } from "./Clustering"; */
import {
    getClusters,
    getModuleById,
    getResourceIndex,
    getStudentName,
} from "../../utils/dataRetrieval";
import { IntlShape } from "react-intl";
import { Data } from "../../interfaces/Data";
import { formatDuration, timeI18n } from "../../utils/format";

export const GROUPS_MEAN_SCORE_ID = "groupsMeanScore";

export interface Cluster {
    id: string;
    membersInfo: MemberInfo[];
}
export type MemberInfo = ClusterAverage | ClusterMember;
type ClusterAverage = MemberInfoCommon & { type: "group" };
type ClusterMember = MemberInfoCommon & { type: "student" };
export type MemberInfoCommon = {
    id: string;
    name: string;
    exerciseResponseTime: string;
    objectivesNbr: number;
    averageScore: string;
};
export type AggregatedInfo = {
    id: typeof GROUPS_MEAN_SCORE_ID;
    membersInfo: ClusterAverage[];
};

export const generateClustering = (
    clustering: ClassroomsClustering,
    classrooms: ClassroomAnalytics[],
    classroomId: string,
    moduleId: string,
    intl: IntlShape
): (AggregatedInfo | Cluster)[] | undefined => {
    const classroomClusters =
        clustering[classroomId] &&
        clustering[classroomId][moduleId] &&
        !clustering[classroomId][moduleId].error
            ? getClusters(clustering[classroomId][moduleId] as ModuleCluster)
            : undefined;

    if (!classroomClusters) return;

    return clustersDataAggregation(
        clustering,
        classroomClusters,
        classrooms,
        classroomId,
        moduleId,
        intl
    );
};

export const clustersDataAggregation = (
    clustering: ClassroomsClustering,
    classroomClusters: ClusterInfosClustering[],
    classrooms: ClassroomAnalytics[],
    classroomId: string,
    moduleId: string,
    intl: IntlShape
): (AggregatedInfo | Cluster)[] => {
    const clustersInfo: (AggregatedInfo | Cluster)[] = [];

    const aggregatedInfo: AggregatedInfo = {
        id: GROUPS_MEAN_SCORE_ID,
        membersInfo: [],
    };

    classroomClusters.forEach((classroomCluster) => {
        const membersInfo: MemberInfo[] = [];

        const groupCluster: MemberInfo = {
            type: "group",
            id: classroomCluster.id,
            name: convertNbrToLetter(Number(classroomCluster.name)),
            exerciseResponseTime: formatDuration(
                Math.floor(Number(classroomCluster.var2)),
                timeI18n(intl)
            ),
            objectivesNbr: Number(classroomCluster.var3),
            averageScore: classroomCluster.var1 + "/1",
        };

        aggregatedInfo.membersInfo.push(groupCluster);
        membersInfo.push(groupCluster);

        classroomCluster.eleves.forEach((studentId) => {
            const studentName = getStudentName(
                classrooms,
                classroomId,
                moduleId,
                studentId
            );
            const studentData = getStudent(
                clustering[classroomId][moduleId] as ModuleCluster,
                studentId
            );
            membersInfo.push({
                type: "student",
                id: studentId,
                name: studentName,
                exerciseResponseTime: formatDuration(
                    Math.floor(Number(studentData.var2)),
                    timeI18n(intl)
                ),
                objectivesNbr: Number(studentData.var3),
                averageScore: studentData.var1 + "/1",
            });
        });
        clustersInfo.push({
            id: classroomCluster.id,
            membersInfo: membersInfo,
        });
    });
    // We want aggregatedInfo only if there is more than 1 cluster
    if (clustersInfo.length > 1) {
        clustersInfo.unshift(aggregatedInfo);
    }
    return clustersInfo;
};

const getStudent = (
    clustering: ModuleCluster,
    studentId: string
): InfosEleves => {
    return clustering.infosEleves.reduce((studentsObject, studentObject) => {
        Object.keys(studentObject).forEach((key) => {
            const studentId = studentObject[key].id;
            studentsObject[studentId] = studentObject[key];
        });
        return studentsObject;
    }, {} as { [studentId: string]: InfosEleves })[studentId];
};

export const getModuleName = (
    moduleId: string | undefined,
    data: Data
): string => {
    if (!moduleId) return "";
    try {
        const module = getModuleById(moduleId, data);
        const index = getResourceIndex(module, data, "module");
        const moduleTitle = module.title.short!;
        if (index && moduleTitle) return `${index}. ${moduleTitle}`;
    } catch (err) {
        //console.log("getModuleName ERROR", err);
        //return "Can't find Module Name";
        //return `Not Found: ${moduleId}`;
    }
    return "";
};

export const getClusterName = (
    clusterId: string | undefined,
    clusters: (AggregatedInfo | Cluster)[],
    intl: IntlShape,
    messages: { [i18nKey: string]: { id: string; defaultMessage: string } }
) => {
    const cluster = clusters.find((cluster) => cluster.id === clusterId);
    if (cluster) {
        if (clusterId === GROUPS_MEAN_SCORE_ID) {
            return intl.formatMessage(messages.groupsMeanScore);
        } else {
            return `${
                cluster.membersInfo[0].type === "group" &&
                intl.formatMessage(messages.group)
            } ${cluster.membersInfo[0].name}`;
        }
    }
    return "";
};

const convertNbrToLetter = (num: number) => {
    return String.fromCharCode(num + 64);
};
